/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useMemo } from 'react';
import Select from '../../../../../components/form/elements/select';
import Classifier from '../../../../../enums/classifier';
import { FormComponent } from '../../../../../components/form';
import { map } from 'rxjs/operators';
import { useParams } from 'react-router-dom';
import { useService } from '../../../../../services/services';
import { useTranslation } from 'react-i18next';
import Sector from '../../../../../enums/sector';
import Disabled from '../../../../../components/form/elements/disabled';
import RadioGroup from '../../../../../components/form/elements/radio-group';
import AnimalType from '../../../../../enums/animal-type';
import { Filter } from '../components/filter';
import Watch from '../../../../../components/form/elements/watch';
import { FormMode } from '../../../../../enums/form-mode';
import Animal from '../../../../../interfaces/animal';
import { AnimalEventAnimal } from '../../../../../interfaces/animal-event';

interface Props {
  addRow: (animal: Animal, index?: number) => void;
  setEventType?: (value: AnimalType) => void;
  setAnimals?: (value: Animal[]) => void;
  animals: Animal[];
  showFilters: boolean;
}

function useFormTop({
  addRow,
  setEventType,
  setAnimals,
  animals,
  showFilters,
}: Props) {
  const { farmId, id, mode } = useParams<{
    farmId: string;
    id: string;
    mode: string;
  }>();
  const { t } = useTranslation();

  const transKeyPrefix = 'pages.livestock.animals_and_events.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { animalService, barnService } = useService();
  const top = useMemo<FormComponent[]>(() => {
    const topFilterElements = [
      Select('species', {
        asyncOptions: (value, _loadedOptions, { page, setValue }) => {
          return animalService.species(farmId, { value }).pipe(
            map((response) => {
              if (response.length === 1) {
                setValue('species', response[0]);
              }

              return {
                hasMore: false,
                options: response,
                additional: { page },
              };
            })
          );
        },
        label: tp('animals.species'),
        column: true,
        clearable: false,
        requiredShow: true,
      }),
      Select('barn_ldc_nr', {
        asyncOptions: (_value, _loadedOptions, { page, setValue }) => {
          return barnService
            .index(farmId, {
              page,
            })
            .pipe(
              map((response) => {
                if (response.data.length === 1) {
                  setValue('barn_ldc_nr', {
                    label: response.data[0].ldc_nr.concat(
                      ' - ',
                      response.data[0].location
                    ),
                    value: response.data[0].id,
                  });
                }

                return {
                  hasMore: response.last_page > response.current_page,
                  options: response.data.map((productCategory) => ({
                    ...productCategory,
                    label: productCategory.ldc_nr.concat(
                      ' - ',
                      productCategory.location
                    ),
                    value: productCategory.id,
                  })),
                  additional: { page: response.current_page + 1 },
                };
              })
            );
        },
        label: tp('animals.barn'),
        column: true,
        clearable: false,
        requiredShow: true,
      }),
      Disabled(
        Watch(
          Select('animal_id', {
            asyncOptions: (value, _loadedOptions, { page, getValues }) => {
              const barn_ldc_nr = getValues('barn_ldc_nr')?.ldc_nr;
              const species = getValues('species')?.value;
              const gender = 'S';
              const alreadyAddedAnimalIds = getValues('animals').map(
                (animal: AnimalEventAnimal) => animal.id
              );

              return animalService
                .index(farmId, {
                  page,
                  barn_ldc_nr,
                  species,
                  gender,
                  animal_id_last4: value,
                })
                .pipe(
                  map((response) => {
                    const filteredOptions = response.data.filter(
                      (animal) => !alreadyAddedAnimalIds.includes(animal.id)
                    );

                    return {
                      hasMore: response.last_page > response.current_page,
                      options: filteredOptions.map((animal) => ({
                        label: animal.animal_id,
                        value: animal.id,
                        ...animal,
                      })),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                );
            },
            label: tp('events.grazing.add_animal'),
            cacheOn: [
              'species.value',
              'barn_ldc_nr.value',
              'event_type',
              'animals',
            ],
            column: true,
            requiredShow: true,
          }),
          ({ value }) => {
            if (value.id) {
              const animal: Animal = { ...value };
              if (animals.findIndex((ittem) => ittem.id === animal.id) < 0) {
                addRow(animal);
              }
            }
          }
        ),
        {
          watchOn: ['species', 'barn_ldc_nr', 'animal_id'],
          callback: (watchValues) => {
            const species = watchValues[0]?.value;
            const barn_ldc_nr = watchValues[1]?.label;
            return !(species && barn_ldc_nr);
          },
        }
      ),
    ];
    const elements = [
      Disabled(
        Watch(
          RadioGroup(
            'event_type',
            [
              {
                label: tp('animals.types.seeding'),
                value: AnimalType.seeding,
              },
              {
                label: tp('animals.types.insemination'),
                value: AnimalType.insemination,
              },
            ],
            tp('events.breeding.event_type'),
            true
          ),
          ({ value }) => {
            setEventType(value);
          }
        ),
        {
          watchOn: 'sub_type',
          callback: () => !!id && mode !== FormMode.view,
        }
      ),
    ];

    if (showFilters) {
      elements.splice(1, 0, ...topFilterElements);
    }

    return elements;
  }, []);
  return top;
}

export default useFormTop;
